import { BOW_OFFSET_PX, CRASH_HARSHNESS, MOVEMENT_RADIUS, TURN_TAX } from '../constants.ts';
import { DEFAULT_WIND_FEEL, type WindFeel } from '../environment/wind.ts';
import { applyDamage, createHullState, reportsFromContacts, withCrossingAngle } from '../damage.ts';
import type { MapDocument } from '../map/types.ts';
import { createBoatState, resolveMove } from '../movement.ts';
import {
  advanceRace,
  calculatedTimeMs,
  createRaceState,
  finishProgress,
  splitKeyId,
  splitKeyRank,
  splitsFromMove,
  type RaceSplit,
} from '../race.ts';
import type { ReachAxes } from '../boat/reach.ts';
import { DEFAULT_HULL, type HullDimensions } from '../hullSize.ts';
import {
  boatCollisionsActive,
  separateFleet,
  type BoatHit,
  type FleetRun,
} from '../modules/host.ts';
import { progressNearestPoint, projectMovePath, type MovePath } from '../path.ts';
import {
  completeResolve,
  tryCommitFleet,
  type CommitResult,
  type FleetContext,
} from '../turn.ts';
import { reverseParkHeading, shouldMarkParked } from '../park.ts';
import { seatCommitArgs, type TurnFeel } from '../turnFeel.ts';
import type { BoatState, Vec2 } from '../types.ts';
import { clampCompetitorCount } from './count.ts';
import type {
  CompetitorId,
  CompetitorPlacement,
  CompetitorResult,
  CompetitorState,
  MatchMovement,
  MatchState,
  SharedSplit,
} from './types.ts';

/**
 * Still owed a planning turn: still racing, or finished and parking while someone else
 * is still on course. When the last boat crosses the goal, everyone is frozen (`parked`).
 */
export function seatNeedsTurn(seat: CompetitorState): boolean {
  return !seat.parked;
}

/**
 * Finished the course and clearing the finish approach while other seats are still racing.
 * The last finisher does not park — the match freezes on that goal crossing.
 */
export function seatIsParking(seat: CompetitorState): boolean {
  return seat.race.complete && !seat.parked;
}

/** Mark every seat parked so nothing keeps moving after the field has finished. */
function freezeField(match: MatchState): MatchState {
  if (!match.competitors.every((seat) => seat.race.complete)) {
    return match;
  }

  return {
    ...match,
    competitors: match.competitors.map((seat) =>
      seat.parked ? seat : { ...seat, parked: true },
    ),
  };
}

export type ActiveCommit = {
  match: MatchState;
  commit: CommitResult;
  /** Seats this move pushed, so the scene can tween them alongside the mover. */
  shoves: ShovedSeat[];
};

/** What a simultaneous round became once every locked path was resolved together. */
export type RoundResolution = {
  match: MatchState;
  runs: FleetRun[];
  contacts: BoatHit[];
};

export type ShovedSeat = {
  id: CompetitorId;
  boat: BoatState;
  /** The drift that boat was pushed along, for its hull tween and wake. */
  path: MovePath | null;
  /**
   * Arc-length fraction of the mover's path where this hull is struck.
   * The drift picture waits until the mover's tween reaches it.
   */
  alongMover: number;
};

function replaceCompetitor(match: MatchState, next: CompetitorState): MatchState {
  return {
    ...match,
    competitors: match.competitors.map((seat) => (seat.id === next.id ? next : seat)),
  };
}

export function createMatch(
  placements: readonly CompetitorPlacement[],
  movement: MatchMovement = 'sequential',
): MatchState {
  const count = clampCompetitorCount(placements.length);
  const used =
    placements.length === 0
      ? [
          {
            position: createBoatState().position,
            heading: createBoatState().heading,
          },
        ]
      : placements.slice(0, count);

  return {
    competitors: used.map((placement, id) => ({
      id,
      boat: createBoatState({
        position: placement.position,
        heading: placement.heading,
        speed: placement.speed ?? 0,
      }),
      race: createRaceState(),
      phase: 'planning',
      moveSpeeds: [],
      finishProgress: null,
      splits: [],
      resolvingRace: null,
      lockIn: null,
      hull: createHullState(),
      parked: false,
      parkTurns: 0,
      parkReverseHeading: null,
    })),
    activeId: 0,
    cycle: 0,
    movement,
  };
}

export function competitorById(
  match: MatchState,
  id: CompetitorId,
): CompetitorState | undefined {
  return match.competitors.find((seat) => seat.id === id);
}

export function activeCompetitor(match: MatchState): CompetitorState {
  return competitorById(match, match.activeId) ?? match.competitors[0]!;
}

export function unfinishedIds(match: MatchState): readonly CompetitorId[] {
  return match.competitors.filter((seat) => !seat.race.complete).map((seat) => seat.id);
}

/**
 * Every seat has crossed the goal. Parking only runs while someone is still racing; the
 * last goal crossing freezes the field and ends the match.
 */
export function matchComplete(match: MatchState): boolean {
  return match.competitors.length > 0 && match.competitors.every((seat) => seat.race.complete);
}

export function officialTimeMs(competitor: CompetitorState): number | null {
  if (competitor.finishProgress === null) {
    return null;
  }

  return calculatedTimeMs(competitor.moveSpeeds.length, competitor.finishProgress);
}

export function rankMatch(match: MatchState): readonly CompetitorResult[] {
  return match.competitors
    .flatMap((seat) => {
      const timeMs = officialTimeMs(seat);

      if (timeMs === null) {
        return [];
      }

      return [
        {
          id: seat.id,
          calculatedTimeMs: timeMs,
          rounds: seat.moveSpeeds.length,
          finishProgress: seat.finishProgress ?? 1,
        },
      ];
    })
    .sort((left, right) => {
      if (left.calculatedTimeMs !== right.calculatedTimeMs) {
        return left.calculatedTimeMs - right.calculatedTimeMs;
      }

      return left.id - right.id;
    });
}

/**
 * Walk from the seat after `fromId`, wrapping. Skip parked boats (finished and waiting).
 * A seat that has crossed the goal but is still parking still gets turns.
 * Returns `fromId` when that seat is the only one still owed a turn.
 */
export function nextActiveId(match: MatchState, fromId: CompetitorId): CompetitorId | null {
  const count = match.competitors.length;

  if (count === 0) {
    return null;
  }

  for (let step = 1; step <= count; step += 1) {
    const id = (fromId + step) % count;
    const seat = match.competitors[id];

    if (seat && seatNeedsTurn(seat)) {
      return id;
    }
  }

  return null;
}

export function commitActive(
  match: MatchState,
  dest: Vec2,
  map: MapDocument,
  radius: number = MOVEMENT_RADIUS,
  crashHarshness: number = CRASH_HARSHNESS,
  turnTax: number = TURN_TAX,
  windFeel: WindFeel = DEFAULT_WIND_FEEL,
  reach: ReachAxes | null = null,
  bowOffsetPx: number = BOW_OFFSET_PX,
  coastPx?: number,
  keptSpeed?: number,
  /** Hull size per seat. Omitted, every boat is the factory skiff. */
  hulls?: ReadonlyMap<CompetitorId, HullDimensions>,
): ActiveCommit | null {
  const active = activeCompetitor(match);

  if (active.phase !== 'planning' || active.parked) {
    return null;
  }

  // A simultaneous lock-in must not consult the other hulls. They have not moved, and
  // they are about to: sweeping them as parked poses would shove a boat the next
  // player can still see, and it would be the wrong contact once their own path
  // exists. The fleet resolver runs in `resolveRound`, with every locked path.
  // Collisions off is the same omission for the whole match: each move has the
  // sea to itself, and the other hull stays where it is.
  const hidden = match.movement === 'simultaneous';
  const parking = active.race.complete;
  const planned = tryCommitFleet(
    active.phase,
    active.boat,
    dest,
    radius,
    map,
    crashHarshness,
    turnTax,
    windFeel,
    reach,
    bowOffsetPx,
    coastPx,
    keptSpeed,
    hidden || !boatCollisionsActive() ? undefined : fleetAround(match, active, hulls),
  );

  if (!planned) {
    return null;
  }

  const commit = planned.commit;
  const racePath = commit.path;
  const nextRace = parking ? active.race : advanceRace(map, active.race, racePath);
  const finishing = !parking && !active.race.complete && nextRace.complete;
  const progress = finishing ? (finishProgress(map, active.race, racePath) ?? 1) : null;
  const rounds = active.moveSpeeds.length + 1;
  const splits = parking
    ? active.splits
    : [...active.splits, ...splitsFromMove(map, active.race, racePath, rounds)];
  const hull = applyDamage(active.hull, commit.reports);
  const parkReverseHeading = finishing
    ? reverseParkHeading(commit.boat.heading)
    : active.parkReverseHeading;

  if (hidden) {
    // The pose stays. Publishing `commit.boat` here would let the next player's
    // hover collide with a hull that has not moved on screen.
    // Park turns / parked settle in `resolveRound` from the landed pose.
    return {
      match: replaceCompetitor(match, {
        ...active,
        phase: 'resolving',
        resolvingRace: nextRace,
        parkReverseHeading,
        lockIn: {
          aimed: projectMovePath(active.boat, dest, 'arc', turnTax) ?? racePath,
          landedIfClear: resolveMove(active.boat, dest, turnTax, keptSpeed),
          splits,
          finishProgress: progress ?? active.finishProgress,
          hull,
        },
      }),
      commit,
      shoves: [],
    };
  }

  const parkTurns = parking ? active.parkTurns + 1 : active.parkTurns;
  const parked = parking
    ? shouldMarkParked(commit.boat, map, parkTurns, parkReverseHeading)
    : active.parked;

  let next = replaceCompetitor(match, {
    ...active,
    boat: commit.boat,
    phase: 'resolving',
    // Park moves must not inflate official rounds / boat-clock.
    moveSpeeds: parking ? active.moveSpeeds : [...active.moveSpeeds, commit.boat.speed],
    finishProgress: progress ?? active.finishProgress,
    splits,
    resolvingRace: nextRace,
    lockIn: null,
    hull,
    parkTurns,
    parked,
    parkReverseHeading,
  });
  const shoves: ShovedSeat[] = [];

  for (const run of planned.runs) {
    if (run.id === active.id || !run.shoved) {
      continue;
    }

    const seat = competitorById(next, run.id);

    if (!seat) {
      continue;
    }

    // Position, heading, and speed only. A boat that did not steer does not
    // score gates, so a shove past a checkpoint leaves it pending; see
    // `docs/BOAT-COLLISION.md`.
    next = replaceCompetitor(next, { ...seat, boat: run.boat });
    shoves.push({
      id: run.id,
      boat: run.boat,
      path: run.path,
      alongMover: shoveAlongMover(commit, run.id),
    });
  }

  return { match: next, commit, shoves };
}

/** Where on the mover's picture this seat is hit. Zero when there is no path yet. */
function shoveAlongMover(commit: CommitResult, seatId: CompetitorId): number {
  const path = commit.path;
  const contact = commit.boatContacts
    .filter((entry) => entry.fromId === seatId || entry.intoId === seatId)
    .sort((left, right) => left.at - right.at)[0];

  if (!path || !contact) {
    return 0;
  }

  return progressNearestPoint(path, contact.point);
}

/**
 * Every other seat as a parked hull. Finished boats stay in: they are still
 * floating on the course and can still be run into.
 */
function fleetAround(
  match: MatchState,
  active: CompetitorState,
  hulls?: ReadonlyMap<CompetitorId, HullDimensions>,
): FleetContext | undefined {
  if (match.competitors.length < 2) {
    return undefined;
  }

  return {
    id: active.id,
    hull: hulls?.get(active.id) ?? DEFAULT_HULL,
    others: match.competitors
      .filter((seat) => seat.id !== active.id)
      .map((seat) => ({
        id: seat.id,
        boat: seat.boat,
        dimensions: hulls?.get(seat.id) ?? DEFAULT_HULL,
      })),
  };
}

/** `commitActive` with the active seat's arguments built by `seatCommitArgs`. */
export function commitActiveWithFeel(
  match: MatchState,
  dest: Vec2,
  map: MapDocument,
  feel: TurnFeel,
  /** Hull size per seat. Omitted, every boat is the factory skiff. */
  hulls?: ReadonlyMap<CompetitorId, HullDimensions>,
): ActiveCommit | null {
  const args = seatCommitArgs(activeCompetitor(match).boat, dest, feel);

  return commitActive(
    match,
    dest,
    map,
    args.radius,
    args.crashHarshness,
    args.turnTax,
    args.windFeel,
    args.reach,
    args.bowOffsetPx,
    args.coastPx,
    args.keptSpeed,
    hulls,
  );
}

export function finishActiveTurn(match: MatchState): MatchState {
  const active = activeCompetitor(match);
  const resolved = completeResolve(active.boat);
  const settled: CompetitorState = {
    ...active,
    boat: resolved.boat,
    phase: 'planning',
    race: active.resolvingRace ?? active.race,
    resolvingRace: null,
  };
  let next = replaceCompetitor(match, settled);

  if (matchComplete(next)) {
    // Last boat through the goal: stop parking and every other move.
    return freezeField(next);
  }

  const nextId = nextActiveId(next, settled.id);

  if (nextId === null) {
    return next;
  }

  return {
    ...next,
    activeId: nextId,
    cycle: next.cycle + (nextId <= settled.id ? 1 : 0),
  };
}

/** Simultaneous: this seat has committed a move that has not played yet. */
export function lockedIn(competitor: CompetitorState): boolean {
  return competitor.resolvingRace !== null;
}

function lowestNeedingTurnId(match: MatchState): CompetitorId | null {
  return match.competitors.find((seat) => seatNeedsTurn(seat))?.id ?? null;
}

/**
 * Simultaneous handoff. Nothing is applied and `cycle` does not move: the seat that just
 * locked stays staged and the next seat still owed a move takes the seat.
 */
export function passLockIn(match: MatchState): MatchState {
  const count = match.competitors.length;

  for (let step = 1; step <= count; step += 1) {
    const id = (match.activeId + step) % count;
    const seat = match.competitors[id];

    if (seat && seatNeedsTurn(seat) && !lockedIn(seat)) {
      return { ...match, activeId: id };
    }
  }

  return match;
}

/** Every seat still owed a turn has locked a move, so the round can play. */
export function roundReady(match: MatchState): boolean {
  const due = match.competitors.filter((seat) => seatNeedsTurn(seat));

  return due.length > 0 && due.every((seat) => lockedIn(seat));
}

/**
 * Hand the seat to the lowest still-racing id for the reveal. Poles, the telemetry plate, and
 * the round counter are one set of objects, so they follow that seat while every hull moves.
 * Nothing is applied — `resolveRound` does that when the animation lands.
 */
export function beginRound(match: MatchState): MatchState {
  const id = lowestNeedingTurnId(match);

  return id === null ? match : { ...match, activeId: id };
}

/**
 * Apply every locked move at once. The paths go through `resolveFleetTurn` together, so two
 * boats that aimed through each other meet on the shared clock instead of each treating the
 * other as parked. Scoring uses the path that came back: a gate past the contact is not
 * crossed, and a boat that did not steer still only takes the shove. One round is one
 * `cycle`, so shared-world weather still re-rolls once per round.
 */
export function resolveRound(
  match: MatchState,
  map: MapDocument,
  crashHarshness: number = CRASH_HARSHNESS,
  turnTax: number = TURN_TAX,
  hulls?: ReadonlyMap<CompetitorId, HullDimensions>,
  clearancePx: number = MOVEMENT_RADIUS,
): RoundResolution {
  const fleet = separateFleet(
    map,
    match.competitors.map((seat) => ({
      id: seat.id,
      boat: seat.boat,
      dimensions: hulls?.get(seat.id) ?? DEFAULT_HULL,
      intended: seat.lockIn?.aimed ?? null,
      landedIfClear: seat.lockIn?.landedIfClear ?? seat.boat,
    })),
    crashHarshness,
    turnTax,
  );
  const runs = new Map(fleet.runs.map((run) => [run.id, run]));
  const settled: MatchState = {
    ...match,
    competitors: match.competitors.map((seat) => {
      const run = runs.get(seat.id);

      if (!run) {
        return seat;
      }

      if (seat.lockIn) {
        return settleSteered(seat, run, map, clearancePx);
      }

      // A parked / waiting hull is still on the course. The shove moves it; it does not score.
      return run.shoved ? { ...seat, boat: run.boat } : seat;
    }),
    cycle: match.cycle + 1,
  };
  if (matchComplete(settled)) {
    return { match: freezeField(settled), runs: fleet.runs, contacts: fleet.hits };
  }

  const nextId = lowestNeedingTurnId(settled);
  const next = nextId === null ? settled : { ...settled, activeId: nextId };

  return { match: next, runs: fleet.runs, contacts: fleet.hits };
}

/** A seat that steered: score the resolved path, not the sea-to-itself preview in `lockIn`. */
function settleSteered(
  seat: CompetitorState,
  run: FleetRun,
  map: MapDocument,
  _clearancePx: number = MOVEMENT_RADIUS,
): CompetitorState {
  const lock = seat.lockIn;
  const racePath = run.path;
  const parking = seat.race.complete;

  if (!lock || !racePath) {
    const landed = lock?.landedIfClear ?? run.boat;
    const boat = completeResolve(landed).boat;
    const parkTurns = parking ? seat.parkTurns + 1 : seat.parkTurns;

    return {
      ...seat,
      boat,
      phase: 'planning',
      moveSpeeds: parking ? seat.moveSpeeds : [...seat.moveSpeeds, landed.speed],
      race: seat.resolvingRace ?? seat.race,
      splits: lock?.splits ?? seat.splits,
      finishProgress: lock ? lock.finishProgress : seat.finishProgress,
      hull: lock?.hull ?? seat.hull,
      resolvingRace: null,
      lockIn: null,
      parkTurns,
      parked: parking
        ? shouldMarkParked(boat, map, parkTurns, seat.parkReverseHeading)
        : seat.parked,
    };
  }

  if (parking) {
    const parkTurns = seat.parkTurns + 1;

    return {
      ...seat,
      boat: run.boat,
      phase: 'planning',
      moveSpeeds: seat.moveSpeeds,
      finishProgress: seat.finishProgress,
      splits: seat.splits,
      race: seat.race,
      resolvingRace: null,
      lockIn: null,
      hull: seat.hull,
      parkTurns,
      parked: shouldMarkParked(run.boat, map, parkTurns, seat.parkReverseHeading),
    };
  }

  const nextRace = advanceRace(map, seat.race, racePath);
  const finishing = !seat.race.complete && nextRace.complete;
  const progress = finishing ? (finishProgress(map, seat.race, racePath) ?? 1) : null;
  const rounds = seat.moveSpeeds.length + 1;
  const reports = reportsFromContacts(map, run.contacts, seat.boat);

  return {
    ...seat,
    boat: run.boat,
    phase: 'planning',
    moveSpeeds: [...seat.moveSpeeds, run.boat.speed],
    finishProgress: progress ?? seat.finishProgress,
    splits: [
      ...seat.splits,
      ...splitsFromMove(map, seat.race, racePath, rounds),
    ],
    race: nextRace,
    resolvingRace: null,
    lockIn: null,
    hull: applyDamage(seat.hull, run.clipped ? withCrossingAngle(reports, run.crossingDeg) : reports),
    parked: false,
    parkTurns: 0,
    parkReverseHeading: finishing
      ? reverseParkHeading(run.boat.heading)
      : seat.parkReverseHeading,
  };
}

export function latestSharedSplit(match: MatchState): SharedSplit | null {
  return sharedSplitAmong(match.competitors);
}

/** Latest milestone every seat has stamped, with each seat's gap to that split's leader. */
export function sharedSplitAmong(
  seats: readonly { id: CompetitorId; splits: readonly RaceSplit[] }[],
): SharedSplit | null {
  if (seats.length < 2) {
    return null;
  }

  const first = seats[0];

  if (!first) {
    return null;
  }

  let best: SharedSplit | null = null;
  let bestRank = -1;

  for (const split of first.splits) {
    const times = new Map<CompetitorId, number>();
    let allHave = true;

    for (const seat of seats) {
      const found = seat.splits.find((entry) => splitKeyId(entry.key) === splitKeyId(split.key));

      if (!found) {
        allHave = false;
        break;
      }

      times.set(seat.id, found.timeMs);
    }

    if (!allHave) {
      continue;
    }

    const rank = splitKeyRank(split.key);

    if (rank < bestRank) {
      continue;
    }

    let leaderId = first.id;
    let leaderTimeMs = times.get(first.id) ?? Number.POSITIVE_INFINITY;

    for (const [id, timeMs] of times) {
      if (timeMs < leaderTimeMs || (timeMs === leaderTimeMs && id < leaderId)) {
        leaderId = id;
        leaderTimeMs = timeMs;
      }
    }

    const behindMs = new Map<CompetitorId, number>();

    for (const [id, timeMs] of times) {
      behindMs.set(id, timeMs - leaderTimeMs);
    }

    best = {
      key: split.key,
      leaderId,
      leaderTimeMs,
      behindMs,
    };
    bestRank = rank;
  }

  return best;
}
